import type { ReportSummary } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { DonutChart } from '../donut-chart.js';

export function ExecutionStatusCard({ execution }: { execution: ReportSummary['execution'] }) {
  const { t } = useTranslation();
  const donut = [
    { key: 'successful', value: execution.byStatus.successful, token: 'pass' as const },
    { key: 'error', value: execution.byStatus.error, token: 'fail' as const },
    { key: 'blocked', value: execution.byStatus.blocked, token: 'warn' as const },
    {
      key: 'open',
      value: execution.byStatus.open + execution.byStatus.repeat,
      token: 'open' as const,
    },
  ];

  return (
    <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-bold text-text-primary">{t('reporting.rep1')}</h2>
        <span className="font-mono text-[10.5px] text-text-muted">REP-1</span>
      </div>
      <p className="mb-3 text-[10px] text-text-muted">{t('reporting.scopePlan')}</p>
      {/* #38 (Finding 36) + #121/Task 2: Die Legende trug vorher nur Symbole (✓/✕/▢/○) und stand
          als schmale Liste neben dem Donut, wodurch rechts viel Leerraum blieb — deshalb zunächst
          farbige Symbole (gleiche Statusfarbe wie im Donut) + Klartext-Label + Zahl über die volle
          Breite (`flex-1`/`justify-between`). Bei der jetzt nur noch halben Kartenbreite
          (Zwei-Spalten-Raster, #121) riss das die Zahl an den rechten Kartenrand. Jetzt stehen
          Symbol, Label und Zahl als zusammenhängende Gruppe ("✓ Erfolgreich 153") ohne erzwungene
          Restbreite. */}
      <div className="flex items-center gap-5">
        <DonutChart segments={donut} ariaLabel={t('reporting.rep1')} />
        <ul className="space-y-1.5 text-xs">
          <li className="flex items-center gap-2">
            <span className="flex items-center gap-2 text-text-body">
              <span aria-hidden="true" className="text-status-pass">
                ✓
              </span>
              {t('reporting.status.successful')}
            </span>
            <span className="font-bold tabular-nums text-text-primary">
              {execution.byStatus.successful}
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center gap-2 text-text-body">
              <span aria-hidden="true" className="text-status-fail">
                ✕
              </span>
              {t('reporting.status.error')}
            </span>
            <span className="font-bold tabular-nums text-text-primary">
              {execution.byStatus.error}
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center gap-2 text-text-body">
              <span aria-hidden="true" className="text-status-warn">
                ▢
              </span>
              {t('reporting.status.blocked')}
            </span>
            <span className="font-bold tabular-nums text-text-primary">
              {execution.byStatus.blocked}
            </span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center gap-2 text-text-body">
              <span aria-hidden="true" className="text-[var(--status-open)]">
                ○
              </span>
              {t('reporting.status.open')}
            </span>
            <span className="font-bold tabular-nums text-text-primary">
              {execution.byStatus.open + execution.byStatus.repeat}
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}
